import type { ClientTraderDefectSummary } from "../../../lib/api/types";
import type { DataQualityRow, LoyaltyRow, MasterProfile, OrderRow } from "../../../types";
import { routePaths } from "../../../routes/navigation";
import {
    buildPath,
    formatIssueCount,
    getContactReadiness,
    getHealthScore,
    getHealthTone,
    getHighestIssueSeverity,
    getLastOrderValue,
    getOrderFrequency,
} from "./shared";
import type {
    Client360JourneyItem,
    Client360Opportunity,
    Client360Snapshot,
    Client360Stat,
    Client360TimelineItem,
} from "./types";

export function buildClient360Snapshot(
    profile: MasterProfile,
    loyaltyCard: LoyaltyRow | null,
    orders: OrderRow[],
    issues: DataQualityRow[]
): Client360Snapshot {
    const healthScore = getHealthScore(profile, loyaltyCard, orders, issues);

    return {
        contactReadiness: getContactReadiness(profile),
        healthScore,
        lastOrderValue: getLastOrderValue(orders),
        loyaltyReadiness: loyaltyCard?.status ?? "Няма карта",
        orderFrequency: getOrderFrequency(orders),
    };
}

export function buildClient360Stats(
    profile: MasterProfile,
    loyaltyCard: LoyaltyRow | null,
    orders: OrderRow[],
    issues: DataQualityRow[],
    traderDefect?: ClientTraderDefectSummary | null
): Client360Stat[] {
    const highestSeverity = getHighestIssueSeverity(issues);
    const omnichannelCoverage = profile.channels.split(" + ").length;
    const snapshot = buildClient360Snapshot(profile, loyaltyCard, orders, issues);

    return [
        {
            label: "Оценка на профила",
            value: `${snapshot.healthScore}/100`,
            helper: "Композитна оценка за готовността на профила за анализ, контакт и оперативно използване.",
            tone: getHealthTone(snapshot.healthScore),
        },
        {
            label: "Омниканално покритие",
            value: `${omnichannelCoverage} канала`,
            helper: "Колко точки на контакт вече са свързани с профила.",
            tone: omnichannelCoverage >= 2 ? "positive" : "neutral",
        },
        {
            label: "Лоялност",
            value: loyaltyCard?.status ?? "Няма карта",
            helper: loyaltyCard
                ? "Текущият статус на картата и готовността за омниканално използване."
                : "Липсва активна карта и профилът губи част от връзката между каналите.",
            tone: loyaltyCard?.status === "Дигитализирана" ? "positive" : loyaltyCard ? "neutral" : "critical",
        },
        {
            label: "Поръчки и ритъм",
            value: orders.length > 0 ? `${orders.length} · ${snapshot.orderFrequency}` : snapshot.orderFrequency,
            helper: orders.length > 0
                ? `Последна видима поръчка: ${orders[0]?.orderNumber ?? "няма"} за ${snapshot.lastOrderValue}.`
                : "Още няма видими вързани поръчки за този профил.",
            tone: orders.length > 0 ? "positive" : "critical",
        },
        {
            label: "Риск и контакт",
            value: issues.length > 0 ? `${issues.length} сигнала · ${snapshot.contactReadiness}` : snapshot.contactReadiness,
            helper: issues.length > 0
                ? `Най-висока тежест: ${highestSeverity}.`
                : "Няма отворени проблеми по качеството на данните.",
            tone: highestSeverity === "Високо" ? "critical" : issues.length > 0 ? "neutral" : "positive",
        },
        {
            label: "Брак от търговеца",
            sublabel: "Брак от рекламации",
            value: traderDefect?.available
                ? `${traderDefect.articlePercent.toFixed(1)}% · ${traderDefect.pricePercent.toFixed(1)}%`
                : "—",
            helper: "% артикули · % стойност от синхронизирани рекламации.",
            tone: (traderDefect?.pricePercent ?? 0) >= 5 ? "critical" : (traderDefect?.pricePercent ?? 0) > 0 ? "neutral" : "positive",
        },
    ];
}

export function buildClient360Timeline(
    profile: MasterProfile,
    loyaltyCard: LoyaltyRow | null,
    orders: OrderRow[],
    issues: DataQualityRow[]
): Client360TimelineItem[] {
    const items: Client360TimelineItem[] = [
        {
            label: "Профил",
            detail: `${profile.profileId} е в статус ${profile.status} и предпочита ${profile.preferredStore}.`,
            tone: profile.status === "Свързан" ? "positive" : profile.status === "Частичен" ? "neutral" : "critical",
        },
    ];

    if (loyaltyCard) {
        items.push({
            label: "Лоялност",
            detail: `${loyaltyCard.cardNumber} е ${loyaltyCard.status.toLowerCase()} с употреба ${loyaltyCard.omnichannelUse}.`,
            tone: loyaltyCard.status === "Дигитализирана" ? "positive" : "neutral",
        });
    } else {
        items.push({
            label: "Лоялност",
            detail: "Все още няма активна карта, която да затвори връзката между профила и омниканалното поведение.",
            tone: "critical",
        });
    }

    if (orders[0]) {
        items.push({
            label: "Последна поръчка",
            detail: `${orders[0].orderNumber} от ${orders[0].channel} е в статус ${orders[0].status} за ${getLastOrderValue(orders)}.`,
            tone: "positive",
        });
    } else {
        items.push({
            label: "Поръчки",
            detail: "Няма свързани поръчки в текущия изглед, което ограничава атрибуцията и 360 историята.",
            tone: "critical",
        });
    }

    if (issues[0]) {
        items.push({
            label: "Риск",
            detail: `${issues[0].issue} от ${issues[0].source} изисква внимание преди следваща сегментация или кампания.`,
            tone: issues[0].severity === "Високо" ? "critical" : "neutral",
        });
    } else {
        items.push({
            label: "Качество на данните",
            detail: "Няма отворени сигнали и профилът е подходящ за следващи маркетингови и оперативни потоци.",
            tone: "positive",
        });
    }

    return items;
}

export function buildClient360Journey(
    profile: MasterProfile,
    loyaltyCard: LoyaltyRow | null,
    orders: OrderRow[],
    issues: DataQualityRow[]
): Client360JourneyItem[] {
    const steps: Client360JourneyItem[] = [];

    steps.push({
        label: "Разпознаване",
        detail: `${profile.customer} е разпознат в ${profile.channels.split(" + ").length} канала и е в статус ${profile.status}.`,
        tone: profile.status === "Свързан" ? "positive" : profile.status === "Частичен" ? "neutral" : "critical",
    });

    steps.push({
        label: "Съгласие и контакт",
        detail: profile.consent === "Липсва"
            ? "Липсва валидно съгласие и профилът не е готов за директни кампании."
            : `Профилът има налично съгласие: ${profile.consent}.`,
        tone: profile.consent === "Липсва" ? "critical" : "positive",
    });

    steps.push({
        label: "Лоялност",
        detail: loyaltyCard
            ? `${loyaltyCard.cardNumber} е в статус ${loyaltyCard.status} с употреба ${loyaltyCard.omnichannelUse}.`
            : "Все още няма лоялност връзка, което оставя клиентската история непълна.",
        tone: loyaltyCard?.status === "Дигитализирана" ? "positive" : loyaltyCard ? "neutral" : "critical",
    });

    steps.push({
        label: "Поръчки",
        detail: orders.length > 0
            ? `Видими са ${orders.length} поръчки. Последната е ${orders[0]?.orderNumber} от ${orders[0]?.channel}.`
            : "Няма вързани поръчки и атрибуцията остава ограничена.",
        tone: orders.length > 0 ? "positive" : "critical",
    });

    steps.push({
        label: "Качество на данните",
        detail: issues.length > 0
            ? `Отворени са ${issues.length} сигнала, които трябва да се затворят преди следваща сегментация.`
            : "Няма отворени сигнали и профилът е стабилен за оперативна работа.",
        tone: issues.length > 0 ? (getHighestIssueSeverity(issues) === "Високо" ? "critical" : "neutral") : "positive",
    });

    return steps;
}

export function buildClient360Opportunities(
    profile: MasterProfile,
    loyaltyCard: LoyaltyRow | null,
    _orders: OrderRow[],
    issues: DataQualityRow[]
): Client360Opportunity[] {
    const opportunities: Client360Opportunity[] = [];

    if (profile.consent === "Липсва") {
        opportunities.push({
            title: "Събери валидно съгласие",
            detail: "Без валидно съгласие профилът не е готов за директна комуникация и ремаркетинг.",
            priority: "Висок",
            ctaLabel: "Към Клиенти",
            ctaPath: buildPath(routePaths.clients, { profile: profile.profileId }),
        });
    }

    if (!loyaltyCard || loyaltyCard.status === "Нужна валидация") {
        opportunities.push({
            title: loyaltyCard ? "Валидирай лоялния профил" : "Добави лоялна връзка",
            detail: loyaltyCard
                ? "Картата има нужда от валидация, за да носи реална стойност в омниканалните сценарии."
                : "Профилът още няма лоялност карта и губи част от омниканалния контекст.",
            priority: loyaltyCard ? "Среден" : "Висок",
            ctaLabel: "Към Лоялност",
            ctaPath: buildPath(routePaths.loyalty, {
                card: loyaltyCard?.cardNumber,
                search: loyaltyCard?.cardNumber ?? profile.customer,
            }),
        });
    }

    if (issues.length > 0) {
        opportunities.push({
            title: "Затвори сигналите за качество",
            detail: issues.length === 1
                ? "Има 1 отворен сигнал, който блокира по-точна сегментация и следващи действия."
                : `Има ${formatIssueCount(issues.length)} отворени, които блокират по-точна сегментация и следващи действия.`,
            priority: getHighestIssueSeverity(issues) === "Високо" ? "Висок" : "Среден",
            ctaLabel: "Към Клиенти",
            ctaPath: buildPath(routePaths.clients, { profile: profile.profileId, inbox: "Висок приоритет" }),
        });
    }

    if (opportunities.length === 0) {
        opportunities.push({
            title: "Профилът е готов за активиране",
            detail: "Идентичността, лоялноста и историята на поръчките изглеждат стабилни за следващи кампании и анализ.",
            priority: "Среден",
            ctaLabel: "Към Анализи",
            ctaPath: buildPath(routePaths.bi, { focus: "Идентичност" }),
        });
    }

    return opportunities.slice(0, 4);
}
